import { Copy, ExternalLink, Loader2, Search } from "lucide-react";
import React from "react";

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PermissionGuard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components";
import { useToast } from "@/components/ui/use-toast";
import {
  getPlatformsByOrganization,
  replicatePlatformToSubPartners,
} from "@/pages/platforms/platform.api";
import type {
  TPlatform,
  TReplicatePlatformResponse,
} from "@/pages/platforms/platform.types";

import { TClientExtended } from "../clients.types";

export type ReplicateSubPartnerOption = Pick<TClientExtended, "id"> & {
  name?: string | null;
};

type ReplicateLpModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentOrganizationId: string;
  projectId: string;
  subPartners: ReplicateSubPartnerOption[];
  sourcePlatforms?: TPlatform[];
  initialPlatformId?: string;
  onReplicated?: () => void;
};

export const ReplicateLpModal: React.FC<ReplicateLpModalProps> = ({
  open,
  onOpenChange,
  parentOrganizationId,
  projectId,
  subPartners,
  sourcePlatforms,
  initialPlatformId,
  onReplicated,
}) => {
  const { toast } = useToast();
  const [platforms, setPlatforms] = React.useState<TPlatform[]>([]);
  const [loadingPlatforms, setLoadingPlatforms] = React.useState(false);
  const [selectedPlatformId, setSelectedPlatformId] = React.useState("");
  const [allSubPartners, setAllSubPartners] = React.useState(true);
  const [partnerSearch, setPartnerSearch] = React.useState("");
  const [selectedSubPartnerIds, setSelectedSubPartnerIds] = React.useState<
    string[]
  >([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<TReplicatePlatformResponse | null>(
    null,
  );

  const sortedSubPartners = React.useMemo(
    () =>
      [...subPartners].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", "pt-BR", {
          sensitivity: "base",
        }),
      ),
    [subPartners],
  );

  const filteredSubPartners = React.useMemo(() => {
    const query = partnerSearch.trim().toLowerCase();
    if (!query) return sortedSubPartners;
    return sortedSubPartners.filter((partner) =>
      (partner.name ?? "").toLowerCase().includes(query),
    );
  }, [sortedSubPartners, partnerSearch]);

  const allFilteredSelected =
    filteredSubPartners.length > 0 &&
    filteredSubPartners.every((partner) =>
      selectedSubPartnerIds.includes(partner.id),
    );

  const eligibleSourcePlatforms = React.useMemo(() => {
    const list = sourcePlatforms ?? platforms;
    return list.filter(
      (platform) =>
        !platform.organization_id ||
        platform.organization_id === parentOrganizationId,
    );
  }, [sourcePlatforms, platforms, parentOrganizationId]);

  React.useEffect(() => {
    if (!open || !parentOrganizationId) return;

    setResult(null);
    setSelectedPlatformId(initialPlatformId ?? "");
    setAllSubPartners(true);
    setPartnerSearch("");
    setSelectedSubPartnerIds([]);

    if (sourcePlatforms) {
      const eligible = sourcePlatforms.filter(
        (platform) =>
          !platform.organization_id ||
          platform.organization_id === parentOrganizationId,
      );
      setPlatforms(sourcePlatforms);
      if (
        initialPlatformId &&
        eligible.some((platform) => platform.id === initialPlatformId)
      ) {
        setSelectedPlatformId(initialPlatformId);
      } else if (eligible.length === 1) {
        setSelectedPlatformId(eligible[0].id);
      } else {
        setSelectedPlatformId("");
      }
      setLoadingPlatforms(false);
      return;
    }

    setLoadingPlatforms(true);

    void (async () => {
      try {
        const data = await getPlatformsByOrganization(parentOrganizationId);
        setPlatforms(data);
        const eligible = data.filter(
          (platform) =>
            !platform.organization_id ||
            platform.organization_id === parentOrganizationId,
        );
        if (
          initialPlatformId &&
          eligible.some((platform) => platform.id === initialPlatformId)
        ) {
          setSelectedPlatformId(initialPlatformId);
        } else if (eligible.length === 1) {
          setSelectedPlatformId(eligible[0].id);
        }
      } catch (error) {
        toast({
          title: "Erro ao carregar LPs",
          description:
            error instanceof Error ? error.message : "Tente novamente.",
          variant: "destructive",
        });
      } finally {
        setLoadingPlatforms(false);
      }
    })();
  }, [open, parentOrganizationId, sourcePlatforms, initialPlatformId, toast]);

  const toggleSubPartner = (id: string, checked: boolean) => {
    setSelectedSubPartnerIds((prev) => {
      if (checked) return [...new Set([...prev, id])];
      return prev.filter((x) => x !== id);
    });
  };

  const toggleFilteredSubPartners = (checked: boolean) => {
    setSelectedSubPartnerIds((prev) => {
      const filteredIds = filteredSubPartners.map((partner) => partner.id);
      if (checked) return [...new Set([...prev, ...filteredIds])];
      return prev.filter((id) => !filteredIds.includes(id));
    });
  };

  const handleReplicate = async () => {
    if (!selectedPlatformId) {
      toast({
        title: "Selecione uma LP",
        description: "Escolha a landing page de origem para replicar.",
        variant: "destructive",
      });
      return;
    }

    if (!allSubPartners && selectedSubPartnerIds.length === 0) {
      toast({
        title: "Selecione parceiros",
        description: "Marque ao menos um subparceiro ou use a opção Todos.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await replicatePlatformToSubPartners(
        selectedPlatformId,
        {
          project_id: projectId,
          ...(allSubPartners
            ? { all_sub_partners: true }
            : { sub_partner_ids: selectedSubPartnerIds }),
        },
      );
      setResult(response);
      onReplicated?.();
      toast({
        title: "Replicação concluída",
        description: `${response.results.length} LP(s) processada(s).`,
      });
    } catch (error) {
      toast({
        title: "Falha na replicação",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: "Link copiado" });
    } catch {
      toast({
        title: "Não foi possível copiar",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Replicar LP para parceiros</DialogTitle>
          <DialogDescription>
            Cria uma cópia da landing page para cada subparceiro participante
            deste projeto, reutilizando os mesmos planos da LP de origem. As
            vendas feitas nessas LPs serão atribuídas ao subparceiro
            correspondente.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label>LP de origem</Label>
              {loadingPlatforms ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando landing pages…
                </div>
              ) : eligibleSourcePlatforms.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma landing page do parceiro principal encontrada neste
                  projeto. Crie uma LP antes de replicar.
                </p>
              ) : (
                <Select
                  value={selectedPlatformId}
                  onValueChange={setSelectedPlatformId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a LP de origem" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleSourcePlatforms.map((platform) => (
                      <SelectItem key={platform.id} value={platform.id}>
                        {platform.landing_page?.title || platform.id}
                        {platform.plans?.length
                          ? ` (${platform.plans.length} plano(s))`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="all-sub-partners"
                  checked={allSubPartners}
                  onCheckedChange={(v) => setAllSubPartners(Boolean(v))}
                />
                <Label htmlFor="all-sub-partners">
                  Replicar para todos os parceiros do projeto ({sortedSubPartners.length})
                </Label>
              </div>

              {!allSubPartners && sortedSubPartners.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhum subparceiro participante neste projeto. Adicione
                  parceiros ao projeto antes de replicar.
                </p>
              ) : !allSubPartners ? (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={partnerSearch}
                      onChange={(event) => setPartnerSearch(event.target.value)}
                      placeholder="Buscar parceiro por nome..."
                      className="pl-9"
                      aria-label="Buscar parceiros"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      {filteredSubPartners.length} de {sortedSubPartners.length}{" "}
                      parceiro(s)
                      {selectedSubPartnerIds.length > 0
                        ? ` · ${selectedSubPartnerIds.length} selecionado(s)`
                        : ""}
                    </span>
                    {filteredSubPartners.length > 0 ? (
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() =>
                          toggleFilteredSubPartners(!allFilteredSelected)
                        }
                      >
                        {allFilteredSelected
                          ? "Desmarcar visíveis"
                          : "Selecionar visíveis"}
                      </button>
                    ) : null}
                  </div>
                  <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                    {filteredSubPartners.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Nenhum parceiro encontrado para &quot;
                        {partnerSearch.trim()}&quot;.
                      </p>
                    ) : (
                      filteredSubPartners.map((sp) => (
                        <div key={sp.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`sp-${sp.id}`}
                            checked={selectedSubPartnerIds.includes(sp.id)}
                            onCheckedChange={(v) =>
                              toggleSubPartner(sp.id, Boolean(v))
                            }
                          />
                          <Label htmlFor={`sp-${sp.id}`} className="font-normal">
                            {sp.name}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              {result.results.filter((r) => r.created).length} criada(s),{" "}
              {result.results.filter((r) => !r.created).length} já existente(s).
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {[...result.results]
                .sort((a, b) =>
                  (a.sub_partner_name ?? "").localeCompare(
                    b.sub_partner_name ?? "",
                    "pt-BR",
                    { sensitivity: "base" },
                  ),
                )
                .map((item) => (
                <div
                  key={item.sub_partner_organization_id}
                  className="border rounded-md p-3 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-sm">
                      {item.sub_partner_name || item.sub_partner_organization_id}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {item.created ? "Nova LP" : "Já existia"}
                    </span>
                  </div>
                  {item.lp_link ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={item.lp_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary underline truncate max-w-[280px]"
                      >
                        {item.lp_link}
                      </a>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => void copyLink(item.lp_link)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        asChild
                      >
                        <a
                          href={item.lp_link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {!result ? (
            <PermissionGuard
              permissions={[
                "platform:create",
                "partners:edit",
                "partner:self:view",
              ]}
            >
              <Button
                onClick={() => void handleReplicate()}
                disabled={
                  isSubmitting ||
                  loadingPlatforms ||
                  eligibleSourcePlatforms.length === 0 ||
                  !selectedPlatformId ||
                  sortedSubPartners.length === 0
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Replicando…
                  </>
                ) : (
                  "Replicar LP"
                )}
              </Button>
            </PermissionGuard>
          ) : (
            <Button onClick={() => setResult(null)}>Nova replicação</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
